/**
 * Order auto-advance hook — reads and toggles a city's auto-advance config
 * (the per-city switch on the orders page that flips stuck "not started" orders
 * to "preparing" after N minutes).
 *
 * Follows the TanStack Query convention used across the newer hooks
 * (useAppVersions/useSliders): a `*Keys` factory, `useQuery` for the read, and
 * `useMutation` that invalidates the cache on success.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCityAutoAdvance, updateCityAutoAdvance } from "services/orderAutoAdvance";

/** Query key factory — keyed per city so each city's config caches separately. */
export const orderAutoAdvanceKeys = {
    all: ["order-auto-advance"],
    city: (cityId) => ["order-auto-advance", String(cityId)],
};

/**
 * @param {number|string|undefined} cityId city to read/write. When falsy the
 *   query is disabled (e.g. a super admin who hasn't picked a city yet).
 */
const useOrderAutoAdvance = (cityId) => {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: orderAutoAdvanceKeys.city(cityId),
        queryFn: () => getCityAutoAdvance(cityId),
        enabled: !!cityId,
        staleTime: 30_000,
    });

    const mutation = useMutation({
        mutationFn: updateCityAutoAdvance,
        // Seed the cache from the PATCH response so the switch reflects the new
        // state immediately — no stale-until-refetch window and no extra GET.
        onSuccess: (data) => {
            queryClient.setQueryData(orderAutoAdvanceKeys.city(cityId), {
                enabled: !!data?.auto_advance_preparing_enabled,
                minutes: data?.auto_advance_preparing_minutes ?? 3,
            });
        },
    });

    /**
     * Persist a change. Callbacks let the caller toast success/failure.
     * @param {{ enabled: boolean, minutes: number }} payload
     * @param {{ onError?: (err: Error) => void, onSuccess?: () => void }} [callbacks]
     */
    const save = (payload, callbacks = {}) => {
        mutation.mutate(
            { cityId, ...payload },
            {
                onError: (err) => callbacks.onError?.(err),
                onSuccess: () => callbacks.onSuccess?.(),
            },
        );
    };

    return {
        data: query.data,
        isLoading: query.isLoading,
        isFetching: query.isFetching,
        save,
        isSaving: mutation.isPending,
    };
};

export default useOrderAutoAdvance;
